import { timeOut } from '@polymer/polymer/lib/utils/async.js';
import { Debouncer } from '@polymer/polymer/lib/utils/debounce.js';

const _globalLookup = {};

function _getGlobalDebouncer(debouncer) {
    // Only check in global lookup if string (key) is provided.
    if (typeof debouncer === 'string') {
        const key = debouncer;

        // See if debouncer already exists under the specified lookup key.
        debouncer = _globalLookup[key];

        // Debouncer doesn't exist yet, so create it.
        if (!debouncer) {
            debouncer = _globalLookup[key] = _createDebouncer();
            debouncer._key = key; // Can tell which debugger is which (just for debugging, can remove if necessary).
        }
    }
    // Otherwise we just leave the debouncer as is.

    return debouncer;
}

function _deleteGlobalDebouncer(debouncer) {
    for (const key in _globalLookup) {
        if (_globalLookup[key] === debouncer) {
            delete _globalLookup[key];
        }
    }
}

function _createDebouncer() {
    const debouncer = new Debouncer();

    // Provide debouncer.then(...) method which is an alias for whichever promise is currently set in debouncer._final,
    // also return the debouncer (rather than the promise) to maintain a controlled interface to debouncer.
    debouncer.then = function (task) {
        // We replace the _final promise with the resulting promise from the 'then' call,
        // this is necessary so the 'then' method works as one would expect (compared to normal promise.then methods)...
        // it allows each 'then' invocation to pass a new (or same) value to the next 'then' call;
        // otherwise each 'then' invocation would get the original promise's value (ie this._result).
        this._final = this._final.then(task);

        return this; // Return the debouncer for chaining purposes.
    };

    return debouncer;
}

/**
 * When you have code that might be invoked multiple times in a short period but you only want it to execute once.
 *
 * Debounce calls are scoped to a debouncer, the debouncer can be specified directly or a key (string) can be specified
 * and the debouncer will be stored/retrieved from a global lookup under that key. Null can be passed in as the
 * debouncer and a new Debouncer object will be created and returned.
 *
 * Debounce returns a promise which will resolve only once the timeout (including any extensions) has expired, and
 * only the Promise returned by the last debounce call for a specific Debouncer will be resolved (not previous ones).
 *
 * Alternatively (or in combination) you can provide an immediateTask to the debounce call (as 3rd argument) which will be
 * executed immediately if the specified Debouncer is not currently active; if the Debouncer is already active then
 * the provided task will be ignored (debounced).
 *
 * These two ways of using debounce allows you to choose whether you want a task to run at the start or end (or both)
 * of a debounce period. Typically debounce flow execute at the end and are used for things like periodically saving after
 * a user types into an input. Alt debounce flow executes at the start and is useful for things like API calls where you
 * want to execute your code immediately but also protect against calling the same things multiple times.
 * 
 * EXAMPLES:
 * // Execute after:
 * debounce('unique.callName:as-debouncer-key', 500).then(() => { console.log('executes last debounce after timeout'); });
 * 
 * // Execute before:
 * debounce('unique.callName:as-debouncer-key', 500, () => { console.log('executes first debounce immediately'); });
 * 
 * // Execute before & after:
 * debounce('unique.callName:as-debounce-key', 500, () => { console.log('executes first debounce immediately'); return 'first'; })
 *      .then((returnvalue) => { console.log('executes last debounce after timeout, with first value: ' + returnvalue); });
 * 
 * // Control your unique debouncer rather than using a global key (_mydebouncer can be undefined/null initially):
 * this._mydebouncer = debounce(this._mydebouncer, 500, () => { console.log('executes first'); }).then(() => { console.log('executes last'); });
 * 
 * @param {?(Debouncer|string)} debouncer Either null/undefined, a debouncer object (as returned by this function), or a key (string) into the global debouncer lookup.
 * @param {!number} timeout How much time to debounce (ignore) multiple calls with the same debouncer/key.
 * @param {?Function} [immediateTask] Optional callback to execute if this is the first time the specified Debouncer is being run (not on subsequent/debounced calls).
 */
function debounce(debouncer, timeout, immediateTask) {
    // Get/init the debouncer as necessary.
    // Should either be a string that will get/create a Debouncer in the global lookup,
    // or it is null/undefined and we will create a new Debouncer,
    // or it is already a Debouncer object and we can just use it,
    // or it's some other object which is invalid.
    debouncer = _getGlobalDebouncer(debouncer) || _createDebouncer();

    // Throw if debouncer wasn't resolved to a Debouncer object.
    if (!(debouncer instanceof Debouncer)) {
        throw new Error('Invalid argument [debouncer], must be a lookup key (string), Polymer.Debouncer object, or null/undefined.');
    }

    // If the debouncer is not active (ie timeout period is not running) then
    // this is the first invocation (for the period) so we do some 'first run' stuff.
    if (!debouncer.isActive()) {
        // The debouncer isn't active (ie just starting) so check if immediateTask is a Function...
        // if it is then execute it since it is the callback for the 'first' debouncer invocation (per period).
        if (immediateTask instanceof Function) {
            debouncer._result = immediateTask(); // Store the result, used in a couple places.
        }
        else {
            // Set the result as whatever the immediateTask value is. It will usually be undefined (when not a Function)
            // but the option to provide a value that is passed to the successful callback could be useful.
            debouncer._result = immediateTask || void 0;
        }
    }
    // Else the debouncer is active, ie this is a subsequent invocation of this debouncer during an active debouncer period.

    // Create a new promise and set it into the debouncer.final property (overwriting any previous/obsolete one that may have been there).
    // External code can use this promise to add an 'afterwards' callback which is executed only if it is the final during the debouncer period.
    // If it is NOT the final invocation during the debouncer period then it will be ignored/overwritten by a subsequent invocation.
    debouncer._final = new Promise((resolver) => {
        // Save the promise's resolver callbacks into the debouncer so it can be used later, as appropriate.
        debouncer._finalResolver = resolver;
    });

    // Do the actual debounce using the Debouncer object and specified timeout.
    Debouncer.debounce(
        debouncer,
        timeOut.after((typeof timeout === 'number') ? timeout : 0),
        // The callback that will be executed once the debouncer's timeout has expired (only once).
        () => {
            // Resolve the final invocation's 'afterwards' task. All previous invocations would have already been ignored/overwritten.
            debouncer._finalResolver(debouncer._result);

            // Cleanup result in case this debouncer is reused in the future.
            debouncer._result = void 0;

            // If the Debouncer exists in the global lookup it will be deleted to prevent memory leakage,
            // if it doesn't exist in the global lookup (ie Debouncer object was specified rather than lookup key)
            // then this will do nothing and it is up to calling code to delete its own Debouncer.
            _deleteGlobalDebouncer(debouncer);
        });

    return debouncer;
}

export { debounce };